// app/src/lib/ocr/invoiceSaver.js — turn an OCR result into Invoice + LineItem rows.
//
// Implements US-1.7 (confirm & edit) and US-1.8 (duplicate detection), then
// hands off to Phase 2 procurement: normalization, price book, alerts, and
// three-way match.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';
import * as normalizer from '../procurement/normalizer.js';
import { bestVendorMatch, matchStrength } from '../procurement/vendorMatch.js';
import * as priceBook from '../procurement/priceBook.js';
import * as priceAlerts from '../procurement/priceAlerts.js';
import * as threeWayMatch from '../procurement/threeWayMatch.js';
import { emitNewVendor } from '../alerts/index.js';
import { markBooksDirty } from '../backup/sharedSnapshot.js';
import { requireWritable } from '../scale/plan.js';

const FUZZY_AMOUNT_TOLERANCE_CENTS = 1;

/**
 * @typedef {Object} OcrResult
 * @property {string=} vendorName
 * @property {string=} vendorId  Supplier chosen from the list rather than typed.
 *   Takes precedence over vendorName — an explicit choice is never re-resolved.
 * @property {string=} invoiceNumber
 * @property {string=} poNumber
 * @property {string=} date
 * @property {string=} dueDate
 * @property {string=} terms
 * @property {number=} subtotal
 * @property {number=} tax
 * @property {number=} freight
 * @property {number=} other
 * @property {number=} total
 * @property {Array<{ sku?:string, description:string, quantity:number, uom?:string, unitPrice:number, extendedPrice:number, discount?:number, lotNumber?:string, taxable?:boolean, bulk?:boolean, confidence?:number, packSize?:string, isDiscount?:boolean }>} lineItems
 * @property {number=} overallConfidence
 */

/**
 * @param {OcrResult} ocr
 * @returns {Promise<{ match: import('../db.js').Invoice, kind: 'exact'|'fuzzy' } | null>}
 */
export async function findDuplicate(ocr) {
  const claims = await currentClaims();
  if (!claims) return null;
  const userId = claims.sub;

  const vendor = await vendorById(userId, ocr.vendorId) || await resolveVendor(userId, ocr.vendorName);
  const target = ocr.total != null ? Math.round(ocr.total * 100) : null;

  // Candidate pool: vendor-scoped when we could resolve the vendor (precise);
  // otherwise fall back to the user's whole book. The fallback closes the gap
  // where a re-scan whose vendor name the OCR read differently — or a vendor not
  // yet on the books — slipped through undetected (e.g. last week's City Produce
  // re-scan). Invoice counts are small, so an in-memory scan is cheap.
  const candidates = vendor
    ? await db.invoices.where('vendorId').equals(vendor.vendorId).filter((i) => i.userId === userId).toArray()
    : await db.invoices.where('userId').equals(userId).toArray();
  if (!candidates.length) return null;

  const amountMatches = (i) => target != null && Math.abs((i.totalCents || 0) - target) <= FUZZY_AMOUNT_TOLERANCE_CENTS;

  // Attach the matched invoice's vendor name so the confirm UI can show both
  // receipts side by side (the raw invoice row stores only vendorId).
  const decorate = async (hit, kind) => {
    let vendorName = vendor?.name;
    if (!vendorName && hit.vendorId) {
      const v = await db.vendors.get(hit.vendorId).catch(() => null);
      vendorName = v?.name;
    }
    return { match: { ...hit, vendorName }, kind };
  };

  // 1. Same invoice number, CORROBORATED by a matching date or amount. A shared
  //    number alone is NOT proof: warehouse clubs (Costco's "A44192523" member
  //    number) reprint a constant account number the OCR mistakes for an invoice
  //    number, so it repeats on every receipt. Require the number AND a matching
  //    date or total. Without a date/total to confirm against, don't flag.
  if (ocr.invoiceNumber) {
    const hit = candidates.find((i) =>
      i.invoiceNumber && i.invoiceNumber === ocr.invoiceNumber &&
      ((ocr.date && i.issuedDate === ocr.date) || amountMatches(i)));
    if (hit) return decorate(hit, 'exact');
  }

  // 2. Same date + amount, no invoice number needed — the classic re-scan of a
  //    numberless receipt. Both must match, so a coincidental same-day/same-total
  //    from a different supplier is the only false-positive risk — and the user
  //    confirms (replace / discard) before anything is written.
  if (ocr.date && target != null) {
    const hit = candidates.find((i) => i.issuedDate === ocr.date && amountMatches(i));
    if (hit) return decorate(hit, 'fuzzy');
  }
  return null;
}

/**
 * Persist invoice + line items from an OCR result. Then run:
 *   1. Item normalization for each line item (auto-link / propose / unmatched).
 *   2. PriceEntry write for every linked line item.
 *   3. Price-change / contract / shrinkflation alerts.
 *   4. Three-way match attempt if the invoice references a PO.
 *
 * @param {OcrResult} ocr
 * @param {{ duplicateOfId?: string, asCreditMemo?: boolean }} [opts]
 */
export async function saveInvoiceFromOcr(ocr, opts = {}) {
  // Write boundary: a read-only context (team viewer, owner on a SECONDARY
  // device, or a lapsed plan) must never create books. On a secondary device the
  // write wouldn't upload and would be wiped on the next pull — so refuse it up
  // front rather than accept data we can't keep. Throws code='READ_ONLY'.
  await requireWritable();
  const claims = await currentClaims();
  if (!claims) throw new Error('saveInvoice: not signed in');
  const userId = claims.sub;

  // An explicit vendorId (the user picked a supplier from the list rather than
  // typing a name) is final: no fuzzy resolution, no chance a near-miss files
  // the invoice under a different supplier than the one they chose.
  let vendor = await vendorById(userId, ocr.vendorId) || await resolveVendor(userId, ocr.vendorName);
  let createdNewVendor = false;
  if (!vendor) {
    vendor = {
      vendorId: crypto.randomUUID(),
      userId,
      name: ocr.vendorName || 'Unknown vendor',
      normalizedName: normalize(ocr.vendorName || ''),
      aliases: [],
      isActive: 1,
    };
    await db.vendors.put(vendor);
    createdNewVendor = true;
  }

  const invoiceId = crypto.randomUUID();
  // Derive the subtotal from the lines when the caller didn't state one.
  // Storing 0 here breaks the invariant the rest of the app relies on —
  // total = subtotal + tax + freight + other — which is what
  // recomputeInvoiceTotals() re-derives after any later line edit.
  const subtotalCents = ocr.subtotal != null
    ? cents(ocr.subtotal)
    : (ocr.lineItems || []).reduce((s, li) => s + cents(li && li.extendedPrice), 0);
  const invoice = {
    invoiceId,
    userId,
    vendorId: vendor.vendorId,
    invoiceNumber: ocr.invoiceNumber,
    poNumber: ocr.poNumber,
    issuedDate: ocr.date || isoToday(),
    dueDate: ocr.dueDate,
    terms: ocr.terms,
    subtotalCents,
    taxCents: cents(ocr.tax),
    freightCents: cents(ocr.freight),
    // Operator's own adjustment to the total (lib/ocr/invoiceTotals.js). Kept
    // as its own field so the total stays reconstructible from its parts —
    // recomputeInvoiceTotals() below re-derives it after any later line edit.
    otherCents: cents(ocr.other),
    totalCents: cents(ocr.total),
    status: 'RECEIVED',
    isDuplicate: !!opts.duplicateOfId,
    duplicateOfId: opts.duplicateOfId,
    createdAt: Date.now(),
  };

  // Build line item rows (itemId left undefined; normalizer fills it after save).
  const lineRows = (ocr.lineItems || []).map((li) => ({
    lineItemId: crypto.randomUUID(),
    userId,
    invoiceId,
    sku: li.sku,
    description: li.description,
    quantity: Number(li.quantity) || 0,
    uom: li.uom,
    packSize: li.packSize,
    unitPriceCents: cents(li.unitPrice),
    extendedPriceCents: cents(li.extendedPrice),
    discountCents: cents(li.discount),
    lotNumber: li.lotNumber,
    taxable: li.taxable ? 1 : 0,
    bulk: li.bulk ? 1 : 0, // sold by weight — exempt from the qty × price check
    confidence: li.confidence,
    category: li.category, // top-level COGS bucket (food|beverages|packaging|other)
    // The aggregated money-off line (Costco instant savings, coupons). It is a
    // real line so its negative amount nets out of COGS, but it is not a
    // product: keep it out of the item catalogue and the price book.
    isDiscount: li.isDiscount ? 1 : 0,
    priceExcluded: li.isDiscount ? 1 : 0,
    itemId: undefined,
  }));

  await db.transaction('rw', db.invoices, db.lineItems, db.alerts, async () => {
    await db.invoices.put(invoice);
    if (lineRows.length) await db.lineItems.bulkPut(lineRows);
    if (opts.duplicateOfId) {
      await db.alerts.add({
        alertId: crypto.randomUUID(),
        userId,
        severity: 'warning',
        kind: 'DUPLICATE_INVOICE',
        title: `Duplicate invoice ${ocr.invoiceNumber || ''} from ${vendor.name}`,
        createdAt: Date.now(),
        dismissed: 0,
      });
    }
  });

  // Phase 2 enrichment runs OUTSIDE the transaction (each step may itself open
  // its own transactions, and we want partial progress preserved on failure).
  for (const row of lineRows) {
    if (row.isDiscount) continue; // money off, not a product — nothing to catalogue
    try {
      const classification = await normalizer.classify({
        vendorId: vendor.vendorId,
        description: row.description,
        sku: row.sku,
      });
      if (classification.status === 'linked' && classification.itemId) {
        row.itemId = classification.itemId;
        await db.lineItems.put(row);
        // Price book + alerts run only on linked items.
        const item = await db.items.get(classification.itemId);
        await priceBook.logEntry({
          vendorId: vendor.vendorId,
          itemId: classification.itemId,
          vendorItemId: classification.vendorItemId,
          price: (row.unitPriceCents || 0) / 100,
          packSize: row.packSize,
          uom: row.uom || 'each',
          effectiveDate: invoice.issuedDate,
          source: 'ocr',
          invoiceId,
        });
        await priceAlerts.checkForAlerts({
          vendorId: vendor.vendorId,
          itemId: classification.itemId,
          vendorName: vendor.name,
          itemName: item?.name || row.description,
          observedPrice: (row.unitPriceCents || 0) / 100,
          observedPackSize: row.packSize,
          effectiveDate: invoice.issuedDate,
          invoiceId,
        });
      } else if (classification.status === 'propose') {
        // Leave itemId unset; UI surfaces the proposal in the review queue.
      }
    } catch (e) {
      // Enrichment must never block invoice save. Log and continue.
      console.warn('[invoiceSaver] enrichment failed for line', e);
    }
  }

  // Three-way match attempt if invoice references a PO.
  if (invoice.poNumber) {
    try { await threeWayMatch.tryMatchByInvoice(invoiceId); }
    catch (e) { console.warn('[invoiceSaver] threeWayMatch failed', e); }
  }

  // §12 NEW_VENDOR info alert for first-time vendors.
  if (createdNewVendor) {
    try { await emitNewVendor(vendor.vendorId); }
    catch (e) { console.warn('[invoiceSaver] emitNewVendor failed', e); }
  }

  // Team accounts: a new invoice changed the books — schedule a shared-snapshot
  // upload (debounced; no-op for viewers, who can't reach this path anyway).
  try { markBooksDirty(); } catch { /* non-fatal */ }

  return { invoiceId };
}

/**
 * Permanently delete an invoice and its line items. Used to discard a confirmed
 * duplicate (or any receipt the owner no longer wants) even after it was saved.
 * @param {string} invoiceId
 */
export async function deleteInvoice(invoiceId) {
  const claims = await currentClaims();
  if (!claims) throw new Error('deleteInvoice: not signed in');
  const userId = claims.sub;
  await db.transaction('rw', db.invoices, db.lineItems, async () => {
    const inv = await db.invoices.get(invoiceId);
    if (!inv || inv.userId !== userId) return;
    await db.lineItems.where('invoiceId').equals(invoiceId).delete();
    await db.invoices.delete(invoiceId);
  });
  try { markBooksDirty(); } catch { /* non-fatal */ }
  return { deleted: true };
}

/**
 * Clear the duplicate flag on an invoice the owner has chosen to keep.
 * @param {string} invoiceId
 */
export async function unmarkDuplicate(invoiceId) {
  const claims = await currentClaims();
  if (!claims) throw new Error('unmarkDuplicate: not signed in');
  const userId = claims.sub;
  const inv = await db.invoices.get(invoiceId);
  if (!inv || inv.userId !== userId) return { updated: false };
  await db.invoices.update(invoiceId, { isDuplicate: 0, duplicateOfId: undefined });
  try { markBooksDirty(); } catch { /* non-fatal */ }
  return { updated: true };
}

/**
 * Edit a saved line item (quantity, unit price, extended/total price, …) and
 * re-derive the parent invoice's subtotal + total. When only quantity or unit
 * price change, the extended price is recomputed unless one is given explicitly.
 * @param {string} lineItemId
 * @param {{ description?:string, quantity?:number, unitPriceCents?:number, extendedPriceCents?:number, uom?:string }} patch
 */
export async function updateLineItem(lineItemId, patch) {
  const claims = await currentClaims();
  if (!claims) throw new Error('updateLineItem: not signed in');
  const userId = claims.sub;
  const line = await db.lineItems.get(lineItemId);
  if (!line || line.userId !== userId) return { updated: false };
  const next = { ...line, ...patch };
  if (patch.extendedPriceCents == null && (patch.quantity != null || patch.unitPriceCents != null)) {
    next.extendedPriceCents = Math.round((Number(next.quantity) || 0) * (Number(next.unitPriceCents) || 0));
  }
  await db.lineItems.put(next);
  await recomputeInvoiceTotals(next.invoiceId);
  try { markBooksDirty(); } catch { /* non-fatal */ }
  return { updated: true, line: next };
}

/**
 * Correct an OCR quantity misread (e.g. "2 cases" read as 1) while KEEPING the
 * printed line total — the dollars on the invoice are right; it's the unit
 * price that's wrong. Recomputes unitPriceCents = extended / quantity so the
 * price history, movers, and plate costs heal together. The pre-fix quantity
 * is stamped once into `originalQuantity` as the audit trail.
 * @param {string} lineItemId
 * @param {number} quantity  the true quantity (> 0)
 */
export async function fixLineItemQuantity(lineItemId, quantity) {
  const qty = Number(quantity);
  if (!Number.isFinite(qty) || qty <= 0) return { updated: false };
  const claims = await currentClaims();
  if (!claims) throw new Error('fixLineItemQuantity: not signed in');
  const line = await db.lineItems.get(lineItemId);
  if (!line || line.userId !== claims.sub) return { updated: false };
  const ext = (line.extendedPriceCents || 0) > 0
    ? line.extendedPriceCents
    : Math.round((Number(line.quantity) || 0) * (line.unitPriceCents || 0));
  const patch = {
    quantity: qty,
    extendedPriceCents: ext,
    unitPriceCents: ext > 0 ? Math.round(ext / qty) : line.unitPriceCents,
  };
  if (line.originalQuantity == null) patch.originalQuantity = line.quantity;
  return updateLineItem(lineItemId, patch);
}

/**
 * Exclude/include a line item from PRICE TRACKING only (price history, movers,
 * price check). The spend itself stays real — COGS and invoice totals are
 * untouched — for points that are wrong in a way a quantity fix can't express.
 * @param {string} lineItemId
 * @param {boolean} excluded
 */
export async function setLineItemPriceExclusion(lineItemId, excluded) {
  const claims = await currentClaims();
  if (!claims) throw new Error('setLineItemPriceExclusion: not signed in');
  const line = await db.lineItems.get(lineItemId);
  if (!line || line.userId !== claims.sub) return { updated: false };
  await db.lineItems.update(lineItemId, { priceExcluded: excluded ? 1 : 0 });
  try { markBooksDirty(); } catch { /* non-fatal */ }
  return { updated: true, priceExcluded: excluded ? 1 : 0 };
}

/**
 * Correct a saved invoice's issue date (US-1.7 edit). OCR occasionally misreads
 * the date — most visibly a date in the FUTURE, which a receipt can never have.
 * `issuedDate` is a YYYY-MM-DD string (or '' to clear). Touches only the date so
 * payment terms / month attribution recompute naturally from the new value.
 * @param {string} invoiceId
 * @param {string} issuedDate  YYYY-MM-DD
 */
export async function updateInvoiceDate(invoiceId, issuedDate) {
  const claims = await currentClaims();
  if (!claims) throw new Error('updateInvoiceDate: not signed in');
  const userId = claims.sub;
  const inv = await db.invoices.get(invoiceId);
  if (!inv || inv.userId !== userId) return { updated: false };
  const next = typeof issuedDate === 'string' ? issuedDate.trim() : '';
  await db.invoices.update(invoiceId, { issuedDate: next });
  try { markBooksDirty(); } catch { /* non-fatal */ }
  return { updated: true, issuedDate: next };
}

/**
 * Move a single receipt to a different vendor — for when OCR filed it under the
 * wrong (or a duplicate) supplier. Pass an existing `vendorId` to reassign to a
 * vendor already on the books, or a `name` to reuse a fuzzy-matching vendor /
 * create a brand-new one. Price-book entries written from THIS invoice follow it
 * to the new vendor so spend and price history stay attributed correctly.
 *
 * Unlike vendor merge, this touches only the one receipt; the source vendor (and
 * its other receipts) are left intact.
 *
 * @param {string} invoiceId
 * @param {{ vendorId?: string, name?: string }} target
 * @returns {Promise<{ reassigned: boolean, vendorId?: string, vendorName?: string, createdNewVendor?: boolean }>}
 */
export async function reassignInvoice(invoiceId, target = {}) {
  await requireWritable();
  const claims = await currentClaims();
  if (!claims) throw new Error('reassignInvoice: not signed in');
  const userId = claims.sub;

  const inv = await db.invoices.get(invoiceId);
  if (!inv || inv.userId !== userId) return { reassigned: false };

  // Resolve the destination vendor. An explicit id wins; otherwise reuse a
  // matching vendor by name (or mint one) exactly like the OCR save path.
  let vendor = null;
  let createdNewVendor = false;
  if (target.vendorId) {
    vendor = await db.vendors.get(target.vendorId);
    if (!vendor || vendor.userId !== userId) throw new Error('reassignInvoice: target vendor not found');
  } else {
    const name = (target.name || '').trim();
    if (!name) throw new Error('reassignInvoice: vendorId or name required');
    vendor = await resolveVendor(userId, name);
    if (!vendor) {
      vendor = {
        vendorId: crypto.randomUUID(), userId, name,
        normalizedName: normalize(name), aliases: [], isActive: 1,
      };
      await db.vendors.put(vendor);
      createdNewVendor = true;
    }
  }

  const fromVendorId = inv.vendorId;
  if (fromVendorId === vendor.vendorId) {
    return { reassigned: false, vendorId: vendor.vendorId, vendorName: vendor.name };
  }

  await db.transaction('rw', db.invoices, db.priceEntries, async () => {
    await db.invoices.update(invoiceId, { vendorId: vendor.vendorId });
    // Re-point this invoice's price-book entries (no invoiceId index, so scan the
    // old vendor's entries — a small set — and reassign the ones from this doc).
    if (fromVendorId) {
      const entries = await db.priceEntries.where('vendorId').equals(fromVendorId)
        .filter((e) => e.invoiceId === invoiceId).toArray();
      for (const e of entries) { e.vendorId = vendor.vendorId; await db.priceEntries.put(e); }
    }
  });

  if (createdNewVendor) {
    try { await emitNewVendor(vendor.vendorId); }
    catch (e) { console.warn('[invoiceSaver] emitNewVendor (reassign) failed', e); }
  }
  try { markBooksDirty(); } catch { /* non-fatal */ }
  return { reassigned: true, vendorId: vendor.vendorId, vendorName: vendor.name, createdNewVendor };
}

/** Recompute subtotal (sum of line extendeds) and total (+ tax + freight + other). */
async function recomputeInvoiceTotals(invoiceId) {
  const inv = await db.invoices.get(invoiceId);
  if (!inv) return;
  const lines = await db.lineItems.where('invoiceId').equals(invoiceId).toArray();
  const subtotalCents = lines.reduce((s, l) => s + (l.extendedPriceCents || 0), 0);
  // `otherCents` must be included or an operator's corrected total silently
  // reverts the next time any line on the invoice is edited.
  const totalCents = subtotalCents + (inv.taxCents || 0) + (inv.freightCents || 0) + (inv.otherCents || 0);
  await db.invoices.update(invoiceId, { subtotalCents, totalCents });
}

/** A vendor the caller named outright, scoped to this user. Null if unknown. */
async function vendorById(userId, vendorId) {
  if (!vendorId) return null;
  const v = await db.vendors.get(vendorId).catch(() => null);
  return v && v.userId === userId ? v : null;
}

async function resolveVendor(userId, name) {
  if (!name) return null;
  const norm = normalize(name);
  const all = await db.vendors.where('userId').equals(userId).toArray();

  // 1. Exact match on normalized name or a recorded alias (fast, unambiguous).
  const exact = all.find((v) =>
    v.normalizedName === norm || normalize(v.name) === norm || (v.aliases || []).some((a) => normalize(a) === norm));
  if (exact) return exact;

  // 2. Fuzzy: the OCR'd name is a slight variant of an existing vendor
  //    ("Lam's Seafood" → "Lam's Seafood Market"). Reuse that vendor instead of
  //    creating a duplicate. Only LEARN this spelling as a permanent alias when
  //    the match is structurally strong (exact/containment/token overlap) — NOT
  //    on a mere edit-distance guess, which can be a false positive that would
  //    then poison the exact-match fast path for every future scan. A weak match
  //    is reused for this invoice but re-evaluated fresh next time.
  const fuzzy = bestVendorMatch(name, all);
  if (fuzzy) {
    const strong = [fuzzy.name, ...(fuzzy.aliases || [])].some((c) => {
      const s = matchStrength(name, c);
      return s === 'exact' || s === 'strong';
    });
    if (strong && !(fuzzy.aliases || []).some((a) => normalize(a) === norm)) {
      const aliases = [...(fuzzy.aliases || []), name];
      try { await db.vendors.update(fuzzy.vendorId, { aliases }); fuzzy.aliases = aliases; }
      catch (e) { console.warn('[invoiceSaver] alias learn failed', e); }
    }
    return fuzzy;
  }
  return null;
}

function normalize(s) { return (s || '').toLowerCase().replace(/[^a-z0-9]/g, ''); }
function cents(n) { return n == null ? 0 : Math.round(Number(n) * 100); }
function isoToday() { return new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10); }
